/**
 * Visual presets for the sale-screen department picker - icon + accent
 * color keyed by lowercased department name.
 *
 * Resolution chain (V0048 / #145):
 *   1. ``Department.icon_slug`` / ``accent_color`` from the backend.
 *      super_admin / partner_admin sets these per row via the
 *      platform-catalog editor; resolved via COALESCE(location
 *      override, catalog default) on the wire.
 *   2. Frontend name-keyed alias map below - fallback for the
 *      canonical V0020 verticals (kept so locations that haven't
 *      configured anything still get a sensible tile).
 *   3. ``DEFAULT_PRESET`` - neutral CreditCard / slate tile so
 *      the picker degrades gracefully on a fresh / custom vertical.
 *
 * Add a department here only when an installer ships a new vertical
 * we want to render without a DB-side config edit. Match by
 * lowercased trimmed name; aliases (e.g. "F&I" vs "finance") expand
 * into the same preset.
 */

import {
  Activity,
  AlertTriangle,
  Briefcase,
  Car,
  Cookie,
  CreditCard,
  Hammer,
  HeartPulse,
  Leaf,
  Package,
  Pill,
  ShoppingBag,
  Stethoscope,
  Store,
  Tag,
  Truck,
  Undo2,
  Utensils,
  Wine,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export interface DepartmentPreset {
  /** Tailwind background class for the icon disc. */
  readonly iconBg: string;
  /** Tailwind text color for the icon glyph. */
  readonly iconFg: string;
  /** Hex color for the top accent bar - used inline because we want
   *  the bar to read as a department-specific brand cue, not a
   *  Tailwind utility color. Pre-vetted for AA contrast against
   *  the white tile. */
  readonly accent: string;
  readonly Icon: LucideIcon;
}

const SALES: DepartmentPreset = {
  iconBg: "bg-blue-50",
  iconFg: "text-blue-700",
  accent: "#1d4ed8",
  Icon: Tag,
};

const PARTS: DepartmentPreset = {
  iconBg: "bg-emerald-50",
  iconFg: "text-emerald-700",
  accent: "#047857",
  Icon: ShoppingBag,
};

const SERVICE: DepartmentPreset = {
  iconBg: "bg-rose-50",
  iconFg: "text-rose-700",
  accent: "#be123c",
  Icon: Wrench,
};

const FINANCE: DepartmentPreset = {
  iconBg: "bg-violet-50",
  iconFg: "text-violet-700",
  accent: "#6d28d9",
  Icon: Briefcase,
};

const MOBILE: DepartmentPreset = {
  iconBg: "bg-amber-50",
  iconFg: "text-amber-700",
  accent: "#b45309",
  Icon: Car,
};

const FOOD: DepartmentPreset = {
  iconBg: "bg-orange-50",
  iconFg: "text-orange-700",
  accent: "#c2410c",
  Icon: Utensils,
};

const TAKEOUT: DepartmentPreset = {
  iconBg: "bg-amber-50",
  iconFg: "text-amber-700",
  accent: "#a16207",
  Icon: ShoppingBag,
};

const DELIVERY: DepartmentPreset = {
  iconBg: "bg-indigo-50",
  iconFg: "text-indigo-700",
  accent: "#3730a3",
  Icon: Truck,
};

const CATERING: DepartmentPreset = {
  iconBg: "bg-fuchsia-50",
  iconFg: "text-fuchsia-700",
  accent: "#86198f",
  Icon: Cookie,
};

const PRIMARY_CARE: DepartmentPreset = {
  iconBg: "bg-teal-50",
  iconFg: "text-teal-700",
  accent: "#0f766e",
  Icon: Stethoscope,
};

const SPECIALTY: DepartmentPreset = {
  iconBg: "bg-pink-50",
  iconFg: "text-pink-700",
  accent: "#9d174d",
  Icon: HeartPulse,
};

const EMERGENCY: DepartmentPreset = {
  iconBg: "bg-red-50",
  iconFg: "text-red-700",
  accent: "#b91c1c",
  Icon: AlertTriangle,
};

const PHARMACY: DepartmentPreset = {
  iconBg: "bg-purple-50",
  iconFg: "text-purple-700",
  accent: "#7e22ce",
  Icon: Pill,
};

const DIAGNOSTICS: DepartmentPreset = {
  iconBg: "bg-cyan-50",
  iconFg: "text-cyan-700",
  accent: "#0e7490",
  Icon: Activity,
};

const RETAIL: DepartmentPreset = {
  iconBg: "bg-surface-muted",
  iconFg: "text-foreground",
  accent: "#334155",
  Icon: Store,
};

export const DEFAULT_PRESET: DepartmentPreset = {
  iconBg: "bg-surface-muted",
  iconFg: "text-foreground",
  accent: "#475569",
  Icon: CreditCard,
};

/** Aliases let "F&I" / "Finance" / "Finance & Insurance" all resolve
 *  to the same preset without requiring the name to be exact. The key
 *  is lowercased + alpha-only so spacing / punctuation differences
 *  collapse. */
const PRESETS: Record<string, DepartmentPreset> = {
  sales: SALES,
  vehiclesales: SALES,
  newvehicles: SALES,
  usedvehicles: SALES,

  parts: PARTS,
  partsaccessories: PARTS,
  accessories: PARTS,

  service: SERVICE,
  servicerepairs: SERVICE,
  repairs: SERVICE,

  fi: FINANCE,
  finance: FINANCE,
  financeinsurance: FINANCE,

  mobile: MOBILE,
  mobileservice: MOBILE,

  dinein: FOOD,
  food: FOOD,
  foodbeverage: FOOD,
  takeout: TAKEOUT,
  delivery: DELIVERY,
  catering: CATERING,

  primary: PRIMARY_CARE,
  primarycare: PRIMARY_CARE,
  specialty: SPECIALTY,
  specialtycare: SPECIALTY,
  emergency: EMERGENCY,
  emergencyservices: EMERGENCY,
  pharmacy: PHARMACY,
  prescriptions: PHARMACY,
  diagnostics: DIAGNOSTICS,

  retail: RETAIL,
  general: RETAIL,
  generalmerch: RETAIL,
};

/** Look up a preset by department name. Lowercases + strips
 *  non-alphanumerics so "F&I", "f & i", "F  &  I" all match the same
 *  alias key. */
export function presetForDepartment(name: string): DepartmentPreset {
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return PRESETS[key] ?? DEFAULT_PRESET;
}

/** lucide-react icon resolver - keys mirror the canonical lucide
 *  export names (kebab-case from the icon library, camelCase from
 *  the import). Used when the backend serves an explicit
 *  ``icon_slug``; unknown slugs fall back to ``DEFAULT_PRESET.Icon``
 *  so a typoed config never blanks the tile. */
const ICON_REGISTRY: Record<string, LucideIcon> = {
  activity: Activity,
  "alert-triangle": AlertTriangle,
  briefcase: Briefcase,
  car: Car,
  cookie: Cookie,
  "credit-card": CreditCard,
  hammer: Hammer,
  "heart-pulse": HeartPulse,
  leaf: Leaf,
  package: Package,
  pill: Pill,
  "shopping-bag": ShoppingBag,
  stethoscope: Stethoscope,
  store: Store,
  tag: Tag,
  truck: Truck,
  "undo-2": Undo2,
  utensils: Utensils,
  wine: Wine,
  wrench: Wrench,
};

/** Hex → Tailwind preset bg/fg for the icon disc. We don't recompute
 *  contrast from arbitrary hex - pre-vetted accent colors come from
 *  the catalog backfill. When the backend gives us an icon + accent
 *  but no disc colors (which is the usual case), we fall back to
 *  neutral slate-50 / slate-700 so the icon stays legible against
 *  the white tile. The accent bar at the top of the tile uses the
 *  raw hex from the backend. */
function discForAccent(_accent: string): { iconBg: string; iconFg: string } {
  return { iconBg: "bg-slate-50", iconFg: "text-slate-700" };
}

/** Resolve the visual preset for a Department row from the backend.
 *  Honors backend-served ``icon_slug`` / ``accent_color`` when set,
 *  else falls back to the name-keyed preset map. */
export function presetForRow(row: {
  readonly name: string;
  readonly icon_slug?: string | null;
  readonly accent_color?: string | null;
}): DepartmentPreset {
  const named = presetForDepartment(row.name);
  // Both fields blank → use the named preset wholesale.
  if (!row.icon_slug && !row.accent_color) return named;

  const Icon = row.icon_slug ? (ICON_REGISTRY[row.icon_slug] ?? named.Icon) : named.Icon;
  const accent = row.accent_color ?? named.accent;
  // Disc colors: prefer the named preset's vetted pair when the
  // backend left them blank (the common case - operator only set
  // the icon + accent). When the backend supplied an accent but the
  // named preset is the neutral DEFAULT, derive a sensible disc.
  const disc =
    named === DEFAULT_PRESET && row.accent_color
      ? discForAccent(row.accent_color)
      : { iconBg: named.iconBg, iconFg: named.iconFg };

  return { ...disc, accent, Icon };
}
